import { BaseService } from '@services/BaseService';
import { contractService } from '@services/ContractService';
// import { AuthorizationContractCostHistoryViewModel } from '@models/AuthorizationContractCostHistory';
import { AuthorizationChargeBreakdown } from '@models/AuthorizationChargeBreakdown';
import { SwoCviViewModel } from '@models/AuthorizationSwoCvi';
import {
  AuthReportSummaryViewModel,
  SwoAuthoReportSummarySearchViewModel,
  AuthReportFormViewModel
} from '@models/AuthReportSummary';
import { StatusCountViewModel, AuthorizationCountViewModel } from '@models/ServiceWorkOrder';
import { PageViewModel } from '@models/common.models';
import { AuthorizationContractCostHistoryViewModel } from '@models/AuthorizationContractCostHistory';
// import { PurchaseRequestViewModel } from '@models/PurchaseRequest';
import { AuthorizationLine } from '@models/AuthorizationLine';
import { AuthoVersionViewModel } from '@models/AuthorizationPrompt';

class AuthorizationService extends BaseService {
  constructor() {
    super('swo');
  }

  getAuthorizationLines(swoId: number, detailNr?: number, payeeTypeCode?: number, payeeVendorId?: number) {
    let queryString = '';
    if (detailNr !== undefined) {
      queryString = `?detailNr=${detailNr}`;
    }
    if (payeeTypeCode) {
      queryString += queryString ? `&payeeType=${payeeTypeCode}` : `?payeeType=${payeeTypeCode}`;
    }
    if (payeeVendorId !== undefined) {
      queryString += queryString ? `&payeeId=${payeeVendorId}` : `?payeeId=${payeeVendorId}`;
    }

    return this.fetch<AuthorizationLine[]>(`${swoId}/authorization/lines${queryString}`, {
      method: 'GET'
    });
  }

  getAuthReportSummary(swoId: number): Promise<AuthReportSummaryViewModel> {
    return this.fetch<AuthReportSummaryViewModel>(`${swoId}/authoReportSummary`, {
      method: 'GET'
    });
  }

  getCvis(swoId: number): Promise<SwoCviViewModel[]> {
    return this.fetch<SwoCviViewModel[]>(`${swoId}/cvis`, {
      method: 'GET'
    });
  }

  getPurchaseRequestTypes(): Promise<SwoCviViewModel[]> {
    return this.fetch<SwoCviViewModel[]>(`authorization/purchaseRequestTypes`, {
      method: 'GET'
    });
  }

  getStatusCounts(): Promise<StatusCountViewModel> {
    return this.fetch('authorization/authoReportSummmary/statusCount', {
      method: 'GET'
    });
  }

  getAuthorizationCount(): Promise<AuthorizationCountViewModel> {
    return this.fetch('authorization/authoReportSummmary/tradeCount', {
      method: 'GET'
    });
  }

  getAuthoHistory(swoId: number, promptId: number): Promise<any> {
    return this.fetch(`${swoId}/authorization/history?promptId=${promptId}`, {
      method: 'GET'
    });
  }

  saveReportSummary(
    swoId: number,
    saveRequest: AuthReportFormViewModel,
    isSubmitting: boolean
  ): Promise<AuthReportFormViewModel> {
    return this.fetch(swoId + '/authorization/report?shouldSubmit=' + isSubmitting, {
      method: 'POST',
      body: JSON.stringify(saveRequest)
    });
  }

  getAuthoReportFormAnswers(swoId: number, cviCd: number, version?: number): Promise<AuthReportFormViewModel> {
    const queryString = version ? '?versionNumber=' + version : '';
    return this.fetch(swoId + '/authorization/report/' + cviCd + queryString, {
      method: 'GET'
    });
  }

  getAuthoReportVersions(reportId: number, swoDetail: number): Promise<AuthoVersionViewModel[]> {
    return this.fetch<AuthoVersionViewModel[]>(
      `authorization/report/` + reportId + `/versions?swoDetailNr=` + swoDetail,
      {
        method: 'GET'
      }
    );
  }

  searchSwoAuthoReportSummaries(
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string = 'asc',
    isCompleted?: boolean,
    isSubmitted?: boolean,
    trade?: string,
    priority?: string,
    stateAbbr?: string,
    vendorGroup?: string,
    dispatchType?: string
  ): Promise<PageViewModel<SwoAuthoReportSummarySearchViewModel>> {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;
    if (query) {
      query = query.trim();
    }

    var url: string = 'authorization/authoReportSummarySearch';
    var k: string = '';
    var queryParameters = {};
    if (isCompleted != null) {
      k = 'isCompleted';
      queryParameters[k] = isCompleted;
    }
    if (isSubmitted != null) {
      k = 'isSubmitted';
      queryParameters[k] = isSubmitted;
    }
    if (trade != null) {
      k = 'trade';
      queryParameters[k] = trade;
    }
    if (priority != null) {
      k = 'priority';
      queryParameters[k] = priority;
    }
    if (stateAbbr != null) {
      k = 'state';
      queryParameters[k] = stateAbbr;
    }
    if (vendorGroup != null) {
      k = 'vendorGroup';
      queryParameters[k] = vendorGroup;
    }
    if (dispatchType != null) {
      k = 'dispatchType';
      queryParameters[k] = dispatchType;
    }

    if (Object.keys(queryParameters).length > 0) {
      url = url.concat('?');
      for (const key of Object.keys(queryParameters)) {
        url = url.concat(`${key}=${queryParameters[key]}&`);
      }
      url = url.slice(0, url.length - 1);
    }
    let x = JSON.stringify({
      query: query,
      pageNumber: pn,
      pageSize: ps,
      orderBy: orderBy,
      orderDirection: orderDirection
    });
    return this.fetch<PageViewModel<SwoAuthoReportSummarySearchViewModel>>(url, {
      method: 'POST',
      body: x
    });
  }

  public own(swoId: number): Promise<AuthReportSummaryViewModel> {
    const uri: string = `${swoId}/authorization/own`;
    return this.fetch<AuthReportSummaryViewModel>(uri, {
      method: 'POST'
    });
  }

  public release(swoId: number): Promise<AuthReportSummaryViewModel> {
    const uri: string = `${swoId}/authorization/release`;
    return this.fetch<AuthReportSummaryViewModel>(uri, {
      method: 'POST'
    });
  }

  // createPurchaseRequest(swoId: number, purchaseInfo: PurchaseRequestViewModel) {
  //   return this.fetch(`authorization/${swoId}/purchaseRequest`, {
  //     method: 'POST',
  //     body: JSON.stringify(purchaseInfo)
  //   });
  // }

  getChargeBreakdown(swoId: number): Promise<Array<AuthorizationChargeBreakdown>> {
    return this.fetch(`${swoId}/authorization/breakdown`, {
      method: 'GET'
    });
  }

  // TODO: PROPERLY TYPE ALL THIS ONCE I MAKE SOME SENSE OF IT
  getAuthorizationContractCostHistory(contractId: number): Promise<AuthorizationContractCostHistoryViewModel[]> {
    return new Promise((res, rej) => {
      contractService.getCostHistory(contractId).catch(e => rej(e));
    });
  }
}

export const authorizationService = new AuthorizationService();



// WEBPACK FOOTER //
// ./src/_services/AuthorizationService.ts